<template>
  <div>
    <PTransparentLoader :loading="loading">
    <CRow>
      <CCol lg="12">
        <!-- Lista Payouts -->
        <CCard class="no-borders">
          <CCardHeader>
            <div class="d-flex justify-content-between align-items-center">
              <h1 class="main-header mb-0">
                <translate translate-context="admin-payout">Payouts Management</translate>
              </h1>
            </div>
          </CCardHeader>
          <CCardBody class="px-0">
            <AjaxTable
              ref="ajaxTable"
              :fields="fields"
              :data="payouts"
              :fetch="fetchList"
            >
              <!-- Clickable ID -->
              <template #id="{item}">
                <td>
                  <CLink 
                    v-if="canViewDetail"
                    :to="{name: 'adminPayoutDetail', params: {id: item.id}}" 
                    class="font-weight-bold"
                  >
                    #{{ item.id }}
                  </CLink>
                  <span v-else class="font-weight-bold">#{{ item.id }}</span>
                </td>
              </template>

              <!-- Status filter dropdown -->
              <template #status-filter>
                <CSelect
                  :value.sync="statusFilterValue"
                  @update:value="onStatusFilterChange"
                  :options="statusOptions"
                  size="sm"
                />
              </template>

              <template #status="{item}">
                <td>
                  <CBadge :color="getStatusColor(item.status)" class="badge-outline">
                    <div class="d-flex align-items-center">
                      <CIcon :name="getStatusIcon(item.status)" size="custom-size" :height="8" class="mr-2"/>
                      <span class="font-weight-normal">{{ getStatusLabel(item.status) }}</span>
                    </div>
                  </CBadge>
                </td>
              </template>
              
              <template #amount="{item}">
                <td>
                  <div>
                    <strong>{{ item.amount | currency('EUR') }}</strong>
                    <div class="small text-muted" v-if="item.pica_services && item.pica_services.length">
                      {{ item.pica_services.length }} <translate translate-context="admin-payout">services</translate>
                    </div>
                  </div>
                </td>
              </template>
              
              <template #contract_info="{item}">
                <td>
                  <div>
                    <div class="font-weight-bold">{{ item.contract_name || `Contract #${item.contract_id}` }}</div>
                    <div class="small" v-if="item.contract_manager_id && item.contract_manager_name">
                      <CLink :to="{name: 'contract_manager.contracts', params: {id: item.contract_manager_id}}">
                        {{ item.contract_manager_name }}
                      </CLink>
                    </div>
                    <div class="small text-muted" v-else-if="item.contract_manager_email">
                      {{ item.contract_manager_email }}
                    </div>
                  </div>
                </td>
              </template>
              
              <template #created_at="{item}">
                <td>
                  {{ item.created_at | formatDateTime }}
                </td>
              </template>
              
              <template #paid_at="{item}">
                <td>
                  <span v-if="item.paid_at">{{ item.paid_at | formatDateTime }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>
              
              <template #notes="{item}">
                <td>
                  <div v-if="item.notes" class="text-truncate" style="max-width: 200px;" v-c-tooltip="item.notes">
                    {{ item.notes }}
                  </div>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>
              
              <template #invoice="{item}">
                <td>
                  <template v-if="item.invoice">
                    <CLink :href="item.invoice" target="_blank" class="d-flex align-items-center">
                      <CIcon name="cipFile" size="sm" class="mr-1"/>
                      <translate translate-context="admin-payout">Invoice</translate>
                    </CLink>
                  </template>
                  <template v-else>
                    <span class="text-muted">-</span>
                  </template>
                </td>
              </template>
              
              <template #actions="{item}">
                <td>
                  <div class="d-flex align-items-center">
                    <CButton
                      v-if="canMarkPaid && item.status === payoutStatus.pending"
                      color="success"
                      size="sm"
                      variant="outline"
                      @click="handleMarkPayoutAsPaid(item)"
                      class="mr-2"
                    >
                      <CIcon name="cipCheck" size="sm" class="mr-1"/>
                      <translate translate-context="admin-payout">Mark paid</translate>
                    </CButton>
                    
                    <CButton
                      v-if="canDeletePayout && (item.status === payoutStatus.requested || item.status === payoutStatus.pending) && item.is_last_unpaid"
                      color="danger"
                      size="sm"
                      variant="outline"
                      @click="handleDeletePayout(item)"
                      v-c-tooltip="$pgettext('admin-payout', 'Delete payout')"
                    >
                      <CIcon name="cipTrashFull" size="sm"/>
                    </CButton>
                    
                    <CButton
                      v-if="canDownload && item.pica_services && item.pica_services.length"
                      color="info"
                      size="sm"
                      variant="ghost"
                      @click="downloadCSV(item)"
                      v-c-tooltip="$pgettext('admin-payout', 'Download CSV')"
                    >
                      <CIcon name="cipDownload" size="sm"/>
                    </CButton>
                    
                    <span v-if="!canMarkPaid && !canDeletePayout && !canDownload" class="text-muted">-</span>
                  </div>
                </td>
              </template>

            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    </PTransparentLoader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import payoutStatus from './constants'

export default {
  name: 'AdminPayoutList',
  components: {
    AjaxTable,
    PTransparentLoader
  },
  data() {
    return {
      loading: false,
      statusFilterValue: payoutStatus.pending,
      payoutStatus, // Make payoutStatus available in template
      fields: [
        {
          key: 'id',
          label: this.$pgettext('admin-payout', 'ID'),
          _style: 'width: 80px',
          sorter: true,
        },
        {
          key: 'status',
          label: this.$pgettext('admin-payout', 'Status'),
          _style: 'width: 150px',
          filter: true,
        },
        {
          key: 'amount',
          label: this.$pgettext('admin-payout', 'Amount'),
          _style: 'width: 120px',
          sorter: true,
        },
        {
          key: 'contract_info',
          label: this.$pgettext('admin-payout', 'Contract / Manager'),
          _style: 'width: 200px',
        },
        {
          key: 'created_at',
          label: this.$pgettext('admin-payout', 'Created'),
          _style: 'width: 150px',
          sorter: true,
        },
        {
          key: 'paid_at',
          label: this.$pgettext('admin-payout', 'Paid'),
          _style: 'width: 150px',
          sorter: true,
        },
        {
          key: 'notes',
          label: this.$pgettext('admin-payout', 'Notes'),
          _style: 'width: 180px',
        },
        {
          key: 'invoice',
          label: this.$pgettext('admin-payout', 'Invoice'),
          _style: 'width: 100px',
        },
        {
          key: 'actions',
          label: this.$pgettext('admin-payout', 'Actions'),
          _style: 'width: 150px',
        }
      ]
    }
  },
  computed: {
    ...mapGetters('adminPayout', ['payouts']),
    ...mapGetters('login', ['hasAnyPerms', 'config']),
    canMarkPaid() {
      return this.hasAnyPerms(['admin.payouts.mark_paid'])
    },
    canViewDetail() {
      return this.hasAnyPerms(['admin.payouts.detail'])
    },
    canDeletePayout() {
      return this.hasAnyPerms(['admin.payouts.delete_last'])
    },
    canDownload() {
      return this.hasAnyPerms(['admin.payouts.download'])
    },
    statusOptions() {
      // Use the payoutStatus constant's method to get properly formatted options
      return payoutStatus.getSelectOptions('admin-payout', true)
    }
  },
  methods: {
    ...mapActions('adminPayout', ['fetchAdminPayoutList', 'markPayoutAsPaid', 'downloadPayoutExcel', 'deleteLastPayout']),
    ...mapActions('login', ['fetchConfig']),
    
    async fetchList(params) {
      // Handle status filter
      const filters = {...params.filters}
      
      // On initial load (no filters), apply default pending filter
      if (Object.keys(filters).length === 0 && !filters.status) {
        filters.status = this.statusFilterValue // which is 'pending' by default
      } else if (filters.status) {
        // If filter comes from AjaxTable column filter
        this.statusFilterValue = filters.status
      } else if (this.statusFilterValue && this.statusFilterValue !== payoutStatus.all) {
        // Apply our dropdown filter
        filters.status = this.statusFilterValue
      }
      
      // Remove status filter if 'all' is selected
      if (filters.status === payoutStatus.all) {
        delete filters.status
      }
      
      await this.fetchAdminPayoutList({
        ...params,
        filters
      })
    },
    
    getStatusLabel(status) {
      return payoutStatus.label(status)
    },
    
    getStatusColor(status) {
      return payoutStatus.color(status)
    },
    
    getStatusIcon(status) {
      return payoutStatus.icon(status)
    },
    
    onStatusFilterChange() {
      // Update the filters and trigger refetch
      const filters = this.statusFilterValue === payoutStatus.all 
        ? {} 
        : { status: this.statusFilterValue }
      
      if (this.$refs.ajaxTable) {
        this.$refs.ajaxTable.filters = filters
        this.$refs.ajaxTable.pagination.page = 1
        this.$refs.ajaxTable.fetchList()
      }
    },
    
    async handleMarkPayoutAsPaid(payout) {
      if (!confirm(this.$pgettext('admin-payout', 'Are you sure you want to mark this payout as paid?'))) {
        return
      }
      
      try {
        await this.markPayoutAsPaid({id: payout.id, data: {}})
        await this.$refs.ajaxTable?.fetchList()
        await this.notifySuccess(this.$pgettext('admin-payout', 'Payout marked as paid successfully'))
      } catch (error) {
        await this.notifyError(this.$pgettext('admin-payout', 'Error marking payout as paid'))
      }
    },
    
    async handleDeletePayout(payout) {
      if (!confirm(this.$pgettext('admin-payout', 'Are you sure you want to delete this payout? This action cannot be undone.'))) {
        return
      }
      
      try {
        await this.deleteLastPayout(payout.id)
        await this.$refs.ajaxTable?.fetchList()
        await this.notifySuccess(this.$pgettext('admin-payout', 'Payout deleted successfully'))
      } catch (error) {
        await this.notifyError(this.$pgettext('admin-payout', 'Error deleting payout'))
      }
    },
    
    async downloadCSV(payout) {
      try {
        await this.downloadPayoutExcel(payout.id)
        await this.notifySuccess(this.$pgettext('admin-payout', 'Excel downloaded successfully'))
      } catch (error) {
        await this.notifyError(this.$pgettext('admin-payout', 'Error downloading Excel'))
      }
    }
  },
  async mounted() {
    // Fetch admin config to load permissions
    await this.fetchConfig({type: 'admin_payouts'})
  }
}
</script>